import { PlatformLocation } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KnownRequestInstrumentationEvents, TelemetryProperties } from '@office-start/telemetry-types';
import { BehaviorSubject, Observable } from 'rxjs';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { UploadErrorType, UploadService, UploadSession, UploadSessionState, UploadSource } from '../../upload/models/upload-session.model';
import { UploadConfigService } from '../../upload/upload-config.service';
import { HttpStatusCode } from '../constants/http-status-code.enum';
import { LocalDataSizePipe } from '../directives/local-data-size/local-data-size.pipe';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpDataResponse, InstrumentedHttpPromise } from '../instrumentation/instrumented-http.service';
import { FileItem } from '../models/file-item.model';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { HttpResponseContentType } from '../models/http-response-content-type.enum';
import { CreateStoryAndPublishDocumentRequest, CreateStoryAndPublishDocumentResponse, CreateStoryResponse, SwayAnalytics, SwayAnalyticsResponse } from '../models/sway.model';
import { OfficeDotComOrigin } from '../models/telemetry-constants.model';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { LocService } from '../shared-context/loc.service';
import { UrlParser } from '../utilities/url-parser.utility';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { SchedulerService } from './scheduler.service';
import { SwayConfig, SwayConfigService } from './sway-config.service';
import { UrlAuthenticatorService } from './url-authenticator.service';
import { XhrService, XhrServiceError } from './xhr.service';

class SwayResponseCache {
    private readonly cachedResponsePromises = new Map<string, Promise<InstrumentedHttpDataResponse<any>>>();

    public set(responsePromise: Promise<InstrumentedHttpDataResponse<any>>, endpoint: string, strategy: FetchStrategy) {
        const key = this.key(endpoint, strategy);
        this.cachedResponsePromises.set(key, responsePromise);
    }

    public has(endpoint: string, strategy: FetchStrategy): boolean {
        const key = this.key(endpoint, strategy);
        return this.cachedResponsePromises.has(key);
    }

    public get(endpoint: string, strategy: FetchStrategy): Promise<InstrumentedHttpDataResponse<any>> {
        const key = this.key(endpoint, strategy);
        return this.cachedResponsePromises.get(key);
    }

    public delete(endpoint: string, strategy: FetchStrategy): boolean {
        const key = this.key(endpoint, strategy);
        return this.cachedResponsePromises.delete(key);
    }

    private key(endpoint: string, strategy: FetchStrategy): string {
        return `/${strategy}/${endpoint}`;
    }
}

/**
 * Service that makes requests to Sway apis.
 */
@Injectable({
    providedIn: 'root'
})
export class SwayService extends XhrService implements UploadService<CreateStoryAndPublishDocumentResponse> {

    public readonly swayConfig: SwayConfig;
    private readonly requestConfig: HttpRequestOptions;

    private readonly cachedResponsePromises = new SwayResponseCache();

    constructor(
        swayConfigService: SwayConfigService,
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly urlAuthenticatorService: UrlAuthenticatorService,
        clientStartupConfigService: ClientStartupConfigService,
        private readonly uploadConfigService: UploadConfigService,
        private readonly locService: LocService,
        private readonly localDataSizePipe: LocalDataSizePipe,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.Sway, contextService, officeHomeApiConfigService, platformLocation, scheduler);

        this.swayConfig = swayConfigService.config;
        const clientStartupConfig = clientStartupConfigService.config;
        this.requestConfig = {
            headers: new Map([
                ['X-UserSessionId', clientStartupConfig.sharedClientStartupContext.correlationId],
                ['X-OHP-CorrelationId', clientStartupConfig.sharedClientStartupContext.correlationId]
            ])
        };
    }

    public createNew(): Promise<string> {
        const url = `${this.swayConfig.apiUrl}/json/createStory`;
        return new Promise<string>((resolve, reject) => {
            this.post<CreateStoryResponse>(
                KnownInstrumentationEvents.SwayCreateRequest,
                KnownRequestInstrumentationEvents.SwayCreateRequest,
                url,
                undefined,
                (promise: InstrumentedHttpPromise<CreateStoryResponse>) => {
                    promise
                        .instrument((result, params) => {
                            if (result.ok && (!result.data || !result.data.url)) {
                                params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                            }
                            this.instrumentResponse(result.headers, params);
                        })
                        .then(response => {
                            if (response && response.data) {
                                resolve(this.urlAuthenticatorService.appendPvrAndUpnAuthParams(response.data.url));
                            } else {
                                reject(new XhrServiceError(response, 'Could not parse Sway creation response'));
                            }
                        })
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.WlqQb);
                        });
                },
                reject,
                this.requestConfig
            );
        });
    }

    public upload(file: File, sessionId: string): Observable<UploadSession<CreateStoryAndPublishDocumentResponse>> {
        const uploadSession = new UploadSession<CreateStoryAndPublishDocumentResponse>(sessionId, file, UploadSource.Button);
        const uploadObservable = new BehaviorSubject<UploadSession<CreateStoryAndPublishDocumentResponse>>(uploadSession);

        const updateSessionState = (state: UploadSessionState) => {
            uploadSession.state = state;
            uploadObservable.next(uploadSession);
        };

        uploadSession.cancellationToken.onCancel(() => updateSessionState(UploadSessionState.Cancelled));

        this.verifyFileForUpload(file)
            .then(() => updateSessionState(UploadSessionState.Started))
            .then(() => SwayService.readBase64(file))
            .then(fileContent => {
                const data: CreateStoryAndPublishDocumentRequest = {
                    docBase64: fileContent,
                    documentName: uploadSession.file.name,
                    world: 'VerticalWorld',
                    enhancementId: '',
                    otsClient: OfficeDotComOrigin
                };

                return this.uploadFile(data, uploadSession);
            })
            .then(createStoryResponse => {
                uploadSession.result = createStoryResponse;
                updateSessionState(UploadSessionState.Completed);
            })
            .catch((errorType: UploadErrorType) => {
                uploadSession.error = this.createUploadError(errorType);
                updateSessionState(UploadSessionState.Failed);
            });

        return uploadObservable.asObservable();
    }

    public getThumbnail(swayUrl: string, thumbnailId: string, strategy: FetchStrategy): Promise<string> {
        if (!this.swayConfig.thumbnailsEnabled || !thumbnailId) {
            return Promise.resolve('');
        }

        const thumbnailUrl = `${this.swayConfig.apiUrl}/sway/v1.0/${SwayService.extractLookupId(swayUrl)}/croppedThumbnailImage?imageId=${thumbnailId}&width=198&height=111`;

        return new Promise<string>((resolve, reject) => {
            this.swayGet<Blob>(
                KnownInstrumentationEvents.SwayGetThumbnailRequest,
                KnownRequestInstrumentationEvents.SwayGetThumbnailRequest,
                thumbnailUrl,
                strategy,
                HttpResponseContentType.Blob,
                true)
                .then(response => {
                    if (response && response.data) {
                        SwayService.readAsDataUrl(response.data).then(resolve, reject);
                    } else {
                        resolve(null);
                    }
                },
                reject);
        });
    }

    public getAnalytics(swayUrl: string, strategy: FetchStrategy): Promise<SwayAnalytics> {
        const lookupId = SwayService.extractLookupId(swayUrl);
        return this.fetchAnalyticsForBatch([lookupId], strategy).then(response => SwayService.findAnalytics(response.data, lookupId));
    }

    public prefetchAnalytics(swayUrls: Array<string>, strategy: FetchStrategy): void {
        if (!swayUrls || swayUrls.length === 0) {
            return;
        }

        const lookupIds = swayUrls.map(SwayService.extractLookupId).sort();
        const batches = SwayService.splitToBatches(lookupIds, this.swayConfig.analyticsBatchSize);
        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
            const batch = batches[batchIndex];
            const batchAnalytics = this.fetchAnalyticsForBatch(batch, strategy);
            for (let i = 0; i < batch.length; i++) {
                const url = this.buildUrlForAnalytics([batch[i]]);
                this.cachedResponsePromises.set(batchAnalytics, url, strategy);
                batchAnalytics.catch(() => this.cachedResponsePromises.delete(url, strategy));
            }
        }
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>) {
        super.handleQueryParam(paramNameInLowerCase, value);
        if (paramNameInLowerCase === 'mocksway' && this.mockRequestHeader) {
            this.requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    private static findAnalytics(allAnalytics: Array<SwayAnalyticsResponse>, lookupId: string) : SwayAnalytics {
        if (allAnalytics) {
            for (let i = 0 ; i < allAnalytics.length; i++) {
                if (allAnalytics[i] && allAnalytics[i].id === lookupId) {
                    return allAnalytics[i].analytics;
                }
            }
        }
        return undefined;
    }

    private static extractLookupId(url: string) {
        return UrlParser.getLocation(url).path.split('/').filter(pathPart => pathPart)[0];
    }

    private static splitToBatches<T>(items: Array<T>, maxBatchSize: number): Array<Array<T>> {
        const result: Array<Array<T>> = [];
        const remain = items.slice();
        while (remain && remain.length && maxBatchSize) {
            result.push(remain.splice(0, maxBatchSize));
        }
        return result;
    }

    private static readAsDataUrl(blob: Blob): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.addEventListener('loadend', () => {
                resolve(reader.result as string);
            });
            reader.addEventListener('abort', () => {
                reject();
            });
            reader.addEventListener('error', () => {
                reject();
            });
            reader.readAsDataURL(blob);
        });
    }

    private static readBase64(file: File): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            SwayService.readAsDataUrl(file)
                .then(content => {
                    const base64ContentStart = content.indexOf(',');
                    if (base64ContentStart >= 0) {
                        resolve(content.substring(base64ContentStart + 1));
                    } else {
                        reject(UploadErrorType.Unknown);
                    }
                })
                .catch(() => reject(UploadErrorType.Unknown));
        });
    }

    private createUploadError(errorType: UploadErrorType) {
        const uploadResources = this.uploadConfigService.config.swayResources;
        switch (errorType) {
            case UploadErrorType.FileTooLarge:
                return {
                    title: uploadResources.errorTitle,
                    type: errorType,
                    message: this.locService.format(uploadResources.largeFile, this.localDataSizePipe.transform(this.swayConfig.uploadMaxFileSize))
                };
            case UploadErrorType.InvalidFileType:
                return {
                    title: uploadResources.errorTitle,
                    type: errorType,
                    message: uploadResources.unsupportedFile
                };
            default:
                return {
                    title: uploadResources.errorTitle,
                    type: errorType,
                    message: ''
                };
        }
    }

    private verifyFileForUpload(file: File): Promise<void> {
        const fileExtension = FileItem.getFileExtension(file.name.toLowerCase());
        if (this.swayConfig.uploadSupportedExtensions.indexOf(fileExtension) === -1) {
            return Promise.reject(UploadErrorType.InvalidFileType);
        }

        if (file.size > this.swayConfig.uploadMaxFileSize) {
            return Promise.reject(UploadErrorType.FileTooLarge);
        }

        return Promise.resolve();
    }

    private uploadFile(data: CreateStoryAndPublishDocumentRequest, uploadSession: UploadSession<CreateStoryAndPublishDocumentResponse>): Promise<CreateStoryAndPublishDocumentResponse> {
        const url = `${this.swayConfig.apiUrl}/publishtosway/createstoryandpublishdocument`;

        return new Promise<CreateStoryAndPublishDocumentResponse>((resolve, reject) => {
            this.post<CreateStoryAndPublishDocumentResponse>(
                KnownInstrumentationEvents.SwayCreateStoryAndPublishDocumentRequest,
                KnownRequestInstrumentationEvents.SwayCreateStoryAndPublishDocumentRequest,
                url,
                data,
                (promise: InstrumentedHttpPromise<CreateStoryAndPublishDocumentResponse>) => {
                    promise
                        .instrument((result, params) => {
                            if (result.ok && (!result.data || !result.data.pollingUrl)) {
                                params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                            }
                            this.instrumentResponse(result.headers, params);
                        })
                        .then(response => {
                            if (response && response.data) {
                                resolve(response.data);
                            } else {
                                reject(UploadErrorType.Unknown);
                            }
                        });
                },
                reject,
                this.requestConfig,
                uploadProgressEvent => uploadSession.updateProgress(uploadProgressEvent.loaded),
                null,
                uploadSession.cancellationToken
            );
        });
    }

    private fetchAnalyticsForBatch(lookupIds: Array<string>, strategy: FetchStrategy): Promise<InstrumentedHttpDataResponse<Array<SwayAnalyticsResponse>>> {
        return this.swayGet<Array<SwayAnalyticsResponse>>(
                        KnownInstrumentationEvents.SwayGetAnalytics,
                        KnownRequestInstrumentationEvents.SwayGetAnalytics,
                        this.buildUrlForAnalytics(lookupIds),
                        strategy,
                        HttpResponseContentType.Json,
                        true);
    }

    private swayGet<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        strategy: FetchStrategy,
        responseType: HttpResponseContentType,
        cacheResult = false
    ): Promise<InstrumentedHttpDataResponse<T>> {
        if (cacheResult && this.cachedResponsePromises.has(url, strategy)) {
            return this.cachedResponsePromises.get(url, strategy);
        }

        const getPromise = new Promise<InstrumentedHttpDataResponse<T>>((resolve, reject) => {
            const requestConfig = { ...this.requestConfig };
            requestConfig.responseType = responseType;

            const errorCallback = (error: XhrServiceError<T>) => {
                if (cacheResult) {
                    this.cachedResponsePromises.delete(url, strategy);
                }

                reject(error);
            };

            const callback = (promise: InstrumentedHttpPromise<T>) => {
                promise
                    .instrument((result, params) => {
                        this.instrumentResponse(result.headers, params);
                    })
                    .then(resolve)
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.cwHpN);
                    });
            };
            super.get(
                eventName,
                otelEventName,
                url,
                callback,
                errorCallback,
                requestConfig,
                strategy);
        });

        if (cacheResult) {
            this.cachedResponsePromises.set(getPromise, url, strategy);
        }

        return getPromise;
    }

    private buildUrlForAnalytics(lookupIds: Array<string>): string {
        const lookupIdsParam = encodeURIComponent(lookupIds.join(','));
        return `${this.swayConfig.apiUrl}/api/v1.0/batchanalytics?ids=${lookupIdsParam}`;
    }

    private instrumentResponse(headers: HttpHeaders, params: InstrumentationParams | TelemetryProperties): void {
        if (!headers) {
            return;
        }

        params[InstrumentationParam.RequestId] = headers.get('X-CorrelationId');
        params[InstrumentationParam.PartnerGeo] = headers.get('X-OfficeCluster');
    }
}
